import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";
import TherapistSidebar from "../../components/TherapistSidebar";

const intervalOptions = [
  { label: "Săptămâna curentă", value: "this_week" },
  { label: "Săptămâna trecută", value: "last_week" },
  { label: "Luna curentă", value: "this_month" },
  { label: "Luna trecută", value: "last_month" },
  { label: "Interval custom", value: "custom" },
];

function getStartEndDates(type: string) {
  const today = new Date();
  let start = new Date(), end = new Date();
  const clone = (d: Date) => new Date(d.getTime());

  const getMonday = (d: Date) => {
    const dt = clone(d);
    const day = dt.getDay();
    dt.setDate(dt.getDate() + (day === 0 ? -6 : 1 - day));
    dt.setHours(0,0,0,0);
    return dt;
  };

  if (type === "this_week") {
    start = getMonday(today);
    end = clone(start); end.setDate(start.getDate() + 6);
  } else if (type === "last_week") {
    const lw = clone(today); lw.setDate(lw.getDate() - 7);
    start = getMonday(lw);
    end = clone(start); end.setDate(start.getDate() + 6);
  } else if (type === "this_month") {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
    end = new Date(today.getFullYear(), today.getMonth()+1, 0);
  } else if (type === "last_month") {
    const ym = today.getMonth()===0 ? today.getFullYear()-1 : today.getFullYear();
    const m  = today.getMonth()===0 ? 11 : today.getMonth()-1;
    start = new Date(ym, m, 1);
    end   = new Date(ym, m+1, 0);
  }

  const fmt = (d: Date, t: "start"|"end") => {
    const y = d.getFullYear();
    const M = String(d.getMonth()+1).padStart(2,"0");
    const D = String(d.getDate()).padStart(2,"0");
    return `${y}-${M}-${D}T${t==="start"?"00:00:00":"23:59:59.999"}`;
  };

  return { start_time: fmt(start,"start"), end_time: fmt(end,"end") };
}

interface Entry {
  key: string;
  label: string;
  count: number;
  score: number;
}

const TherapistPointsPage: React.FC = () => {
  const { therapistId } = useParams<{ therapistId: string }>();
  const { token } = useAuth();

  const [intervalType, setIntervalType] = useState("this_week");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [entries, setEntries] = useState<Entry[]>([]);
  const [total, setTotal] = useState(0);
  const [typeMap, setTypeMap] = useState<Record<string,string>>({});
  const [isCollapsed, setIsCollapsed] = useState(() =>
    localStorage.getItem("sidebarCollapsedTherapist") === "true"
  );

  // Încarcă mapările key→value pentru terapie și activități
  useEffect(() => {
    const loadMaps = async () => {
      const [tResp, aResp] = await Promise.all([
        fetch(`${API_BASE_URL}/types/therapy-type`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/types/activities`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);
      const tData = await tResp.json();
      const aData = await aResp.json();
      const map: Record<string,string> = {};
      (tData.data || []).forEach((t: any) => map[t.key] = t.value);
      (aData.data || []).forEach((a: any) => map[a.key] = a.value);
      setTypeMap(map);
    };
    loadMaps();
  }, [token]);

  useEffect(() => {
    const load = async () => {
      if (!therapistId) return;
      let start_time="", end_time="";
      if (intervalType==="custom") {
        if (!customStart||!customEnd) return;
        start_time=`${customStart}T00:00:00`;
        end_time  =`${customEnd}T23:59:59.999`;
      } else {
        const r = getStartEndDates(intervalType);
        start_time=r.start_time; end_time=r.end_time;
      }

      const res = await fetch(
        `${API_BASE_URL}/score-config/details-by-therapist`+
        `?therapist_id=${therapistId}`+
        `&start_time=${encodeURIComponent(start_time)}`+
        `&end_time=${encodeURIComponent(end_time)}`,
        { headers:{ Authorization:`Bearer ${token}` } }
      );
      if (!res.ok) {
        // tratează erorile (404 etc)
        setEntries([]);
        setTotal(0);
        return;
      }
      const data: {
        entries: Record<string,{ count:number,score:number }>,
        total_score: number
      } = await res.json();

      // transformă în array și adaugă label-ul din map
      const arr: Entry[] = Object.entries(data.entries).map(([k,v])=>({
        key:k,
        label: typeMap[k] || k,
        count:v.count,
        score:v.score
      }));
      setEntries(arr);
      setTotal(data.total_score);
    };
    load();
  }, [therapistId, intervalType, customStart, customEnd, token, typeMap]);

  return (
    <div className={`dashboard-container ${isCollapsed?"collapsed":""}`}>
      <TherapistSidebar onCollapseChange={setIsCollapsed}/>
      <div className="page-content">
        <h1 className="page-title">Detaliu punctaj terapeut</h1>

        <div className="interval-container">
          <label>Interval: </label>
          <select value={intervalType} onChange={e=>setIntervalType(e.target.value)}>
            {intervalOptions.map(o=>
              <option key={o.value} value={o.value}>{o.label}</option>
            )}
          </select>
          {intervalType==="custom" && (
            <>
              <input type="date" value={customStart} onChange={e=>setCustomStart(e.target.value)}/>
              <input type="date" value={customEnd}   onChange={e=>setCustomEnd(e.target.value)}/>
            </>
          )}
        </div>

        <div className="table-rounded-container">
          <table className="table-rounded table-therapist-standings">
            <thead>
              <tr>
                <th>Terapie/Activitate</th>
                <th className="text-right">Punctaj</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.key}>
                  <td>{e.label}</td>
                  <td className="text-right">{e.score} p</td>
                </tr>
              ))}
              <tr className="total-therapist">
                <td><strong>Total</strong></td>
                <td className="text-right"><strong>{total} p</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TherapistPointsPage;